// src/services/tokensDocumento.ts
//
// Catálogo ÚNICO de tokens dos modelos de documento.
//
// Antes esta lista vivia só dentro de SidebarElementos.tsx, enquanto o motor de
// geração de PDF tinha o seu próprio mapeamento — por isso o editor oferecia
// campos que o sistema não sabia preencher, e o token cru (ex: {{PESO_KG}})
// acabava impresso no documento enviado ao cliente.
//
// Agora sidebar, motor de render e validador leem todos deste mesmo arquivo.

export type CategoriaToken = 'os' | 'cliente' | 'veiculo' | 'financeiro' | 'proposta' | 'assinatura';

/** Em quais documentos o sistema consegue preencher este token com dado real. */
export type DisponibilidadeToken = 'ambos' | 'os' | 'proposta' | 'indisponivel';

export interface TokenDocumento {
  tag: string;
  label: string;
  exemplo: string;
  categoria: CategoriaToken;
  disponivel: DisponibilidadeToken;
  /** Explica o motivo quando o dado ainda não existe no sistema. */
  observacao?: string;
}

export const TOKENS_DOCUMENTO: TokenDocumento[] = [
  // Assinatura & Segurança
  { tag: '{{ASSINATURA}}', label: 'Assinatura Digital (Imagem)', exemplo: '[Assinatura do Recebedor]', categoria: 'assinatura', disponivel: 'os' },
  { tag: '{{RG_ASSINATURA}}', label: 'RG do Signatário / Recebedor', exemplo: '35.888.999-X', categoria: 'assinatura', disponivel: 'os' },
  { tag: '{{IP_ASSINATURA}}', label: 'IP de Origem do Acesso', exemplo: '187.54.120.89', categoria: 'assinatura', disponivel: 'os' },
  { tag: '{{DATA_ASSINATURA}}', label: 'Data/Hora da Assinatura', exemplo: '23/08/2026 15:45', categoria: 'assinatura', disponivel: 'os' },
  { tag: '{{VALIDACAO_DIGITAL}}', label: 'Selo de Validação Digital', exemplo: 'Documento Validado via login e senha.', categoria: 'assinatura', disponivel: 'os' },
  { tag: '{{NOME_RECEBEDOR}}', label: 'Nome do Recebedor', exemplo: 'Carlos Eduardo Silva', categoria: 'assinatura', disponivel: 'indisponivel', observacao: 'A OS guarda a imagem da assinatura, o RG e o IP — não há campo para o nome de quem recebeu.' },

  // OS & Geral
  { tag: '{{NUMERO_OS}}', label: 'Número da OS', exemplo: 'OS-2026-0089', categoria: 'os', disponivel: 'os' },
  { tag: '{{DATA_EMISSAO}}', label: 'Data de Emissão', exemplo: '23/08/2026', categoria: 'os', disponivel: 'ambos' },
  { tag: '{{SOLICITANTE}}', label: 'Nome do Solicitante (contato principal)', exemplo: 'Carlos Eduardo', categoria: 'os', disponivel: 'ambos' },
  { tag: '{{TELEFONE_SOLICITANTE}}', label: 'Telefone do Solicitante', exemplo: '(11) 98888-7777', categoria: 'os', disponivel: 'ambos' },
  { tag: '{{HORA_INICIAL}}', label: 'Hora Inicial (Chegada)', exemplo: '08:00', categoria: 'os', disponivel: 'os' },
  { tag: '{{HORA_FINAL}}', label: 'Hora Final (Saída)', exemplo: '12:30', categoria: 'os', disponivel: 'os' },
  { tag: '{{TOTAL_HORAS}}', label: 'Total de Horas', exemplo: '04:30:00', categoria: 'os', disponivel: 'os' },
  { tag: '{{STATUS_SERVICO}}', label: 'Status do Serviço', exemplo: 'Finalizado', categoria: 'os', disponivel: 'os' },

  // Cliente
  { tag: '{{CLIENTE_NOME}}', label: 'Nome / Razão Social', exemplo: 'Empresa Exemplo LTDA', categoria: 'cliente', disponivel: 'ambos' },
  { tag: '{{CLIENTE_DOCUMENTO}}', label: 'CNPJ ou CPF do Cliente', exemplo: '12.345.678/0001-90', categoria: 'cliente', disponivel: 'ambos' },
  { tag: '{{CLIENTE_EMAIL}}', label: 'E-mail do Cliente', exemplo: 'contato@cliente.com.br', categoria: 'cliente', disponivel: 'ambos' },
  { tag: '{{CLIENTE_TELEFONE}}', label: 'Telefone do Cliente', exemplo: '(11) 3333-4444', categoria: 'cliente', disponivel: 'ambos' },
  { tag: '{{CLIENTE_ENDERECO}}', label: 'Endereço do Cliente', exemplo: 'Av. Paulista, 1000 - Bela Vista - São Paulo', categoria: 'cliente', disponivel: 'proposta' },
  { tag: '{{ENDERECO_COLETA}}', label: 'Endereço de Coleta/Retirada', exemplo: 'Rua das Flores, 123 - SP', categoria: 'cliente', disponivel: 'os' },
  { tag: '{{ENDERECO_ENTREGA}}', label: 'Endereço de Entrega', exemplo: 'Av. Paulista, 1000 - SP', categoria: 'cliente', disponivel: 'os' },
  { tag: '{{CLIENTE_RG}}', label: 'RG do Cliente', exemplo: '22.333.444-5', categoria: 'cliente', disponivel: 'indisponivel', observacao: 'O cadastro de Pessoa guarda apenas um documento (CPF/CNPJ), não há campo de RG.' },

  // Veículo & Transporte
  { tag: '{{VEICULO_TIPO}}', label: 'Tipo de Veículo', exemplo: 'VUC Baú 3.5T', categoria: 'veiculo', disponivel: 'os' },
  { tag: '{{VEICULO_PLACA}}', label: 'Placa do Veículo', exemplo: 'ABC-1D23', categoria: 'veiculo', disponivel: 'os' },
  { tag: '{{MOTORISTA_NOME}}', label: 'Nome do Motorista', exemplo: 'João Silva', categoria: 'veiculo', disponivel: 'os' },
  { tag: '{{MOTORISTA_DOCUMENTO}}', label: 'Documento do Motorista', exemplo: '123.456.789-00', categoria: 'veiculo', disponivel: 'os' },
  { tag: '{{AJUDANTE_NOME}}', label: 'Ajudante / Equipe', exemplo: 'Ajudante 1 e 2', categoria: 'veiculo', disponivel: 'os' },
  { tag: '{{QTD_AJUDANTE}}', label: 'Quantidade de Ajudantes', exemplo: '2', categoria: 'veiculo', disponivel: 'os' },
  { tag: '{{OBSERVACOES}}', label: 'Observações / Ocorrências', exemplo: 'Carga frágil, manusear com cuidado', categoria: 'veiculo', disponivel: 'ambos' },
  { tag: '{{PESO_KG}}', label: 'Peso Estimado (KG)', exemplo: '1.200 kg', categoria: 'veiculo', disponivel: 'indisponivel', observacao: 'A Ordem de Serviço não tem campo de peso cadastrado hoje.' },
  { tag: '{{VOLUMES_QTD}}', label: 'Qtd de Volumes', exemplo: '45 caixas', categoria: 'veiculo', disponivel: 'indisponivel', observacao: 'A Ordem de Serviço não tem campo de volumes cadastrado hoje.' },
  { tag: '{{VALOR_CARGA}}', label: 'Valor Declarado da Carga', exemplo: 'R$ 25.000,00', categoria: 'veiculo', disponivel: 'indisponivel', observacao: 'Valor da carga só existe no CT-e, não na Ordem de Serviço.' },

  // Financeiro
  { tag: '{{VALOR_FRETE}}', label: 'Valor do Frete Base', exemplo: '1.450,00', categoria: 'financeiro', disponivel: 'os' },
  { tag: '{{VALOR_AJUDANTE}}', label: 'Valor Unitário do Ajudante', exemplo: '120,00', categoria: 'financeiro', disponivel: 'os' },
  { tag: '{{SUBTOTAL_AJUDANTE}}', label: 'Subtotal dos Ajudantes', exemplo: '240,00', categoria: 'financeiro', disponivel: 'os' },
  { tag: '{{VALOR_TAXAS}}', label: 'Despesas / Pedágios', exemplo: '120,00', categoria: 'financeiro', disponivel: 'ambos' },
  { tag: '{{VALOR_TOTAL}}', label: 'Valor Total da OS/Proposta', exemplo: '1.520,00', categoria: 'financeiro', disponivel: 'ambos' },
  { tag: '{{FORMA_PAGAMENTO}}', label: 'Forma / Condições de Pagamento', exemplo: 'Faturamento quinzenal, 15 dias', categoria: 'financeiro', disponivel: 'proposta' },
  { tag: '{{VALOR_DESCONTO}}', label: 'Desconto Concedido', exemplo: '50,00', categoria: 'financeiro', disponivel: 'indisponivel', observacao: 'Não há campo de desconto na OS nem na Proposta.' },

  // Proposta Comercial
  { tag: '{{PROPOSTA_REF}}', label: 'Referência da Proposta', exemplo: 'PROP-2026-042', categoria: 'proposta', disponivel: 'proposta' },
  { tag: '{{VALIDADE_PROPOSTA}}', label: 'Validade da Proposta', exemplo: '15 dias', categoria: 'proposta', disponivel: 'proposta' },
  { tag: '{{SERVICO_DESCRICAO}}', label: 'Descrição do Serviço', exemplo: 'Transporte dedicado Grande SP', categoria: 'proposta', disponivel: 'ambos' },
  { tag: '{{SERVICO_QTD}}', label: 'Quantidade do Serviço (1º item)', exemplo: '01 rota', categoria: 'proposta', disponivel: 'proposta' },
  { tag: '{{SERVICO_VALOR}}', label: 'Investimento Unitário (1º item)', exemplo: '1.800,00', categoria: 'proposta', disponivel: 'proposta' },
  { tag: '{{TABELA_ITENS}}', label: 'Tabela de Itens (todos os serviços)', exemplo: '[tabela com todos os itens da proposta]', categoria: 'proposta', disponivel: 'proposta', observacao: 'Bloco dinâmico: gera uma linha por item da proposta. Posicione e redimensione o campo para definir a área da tabela.' },
];

/** Tokens que o sistema realmente preenche para um tipo de documento. */
export function tokensSuportados(tipo: 'os' | 'proposta'): Set<string> {
  return new Set(
    TOKENS_DOCUMENTO
      .filter((t) => t.disponivel === 'ambos' || t.disponivel === tipo)
      .map((t) => t.tag.replace(/[{}]/g, ''))
  );
}

/** Extrai todos os tokens usados no canvas_json de um modelo. */
export function extrairTokensDoCanvas(canvasJson: string): string[] {
  const encontrados = new Set<string>();
  const regex = /\{\{\s*([A-Z0-9_]+)\s*\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(canvasJson || '')) !== null) {
    encontrados.add(match[1]);
  }
  return Array.from(encontrados);
}

/**
 * Valida um modelo: aponta tokens que o sistema não sabe preencher, para evitar
 * que um token cru acabe impresso no documento enviado ao cliente.
 */
export function validarTokensDoModelo(
  canvasJson: string,
  tipo: 'ordem_servico' | 'proposta'
): { tokensInvalidos: string[]; tokensIndisponiveis: string[] } {
  const chaveTipo = tipo === 'ordem_servico' ? 'os' : 'proposta';
  const suportados = tokensSuportados(chaveTipo);
  const conhecidos = new Set(TOKENS_DOCUMENTO.map((t) => t.tag.replace(/[{}]/g, '')));

  const usados = extrairTokensDoCanvas(canvasJson);
  const tokensInvalidos = usados.filter((t) => !conhecidos.has(t));
  const tokensIndisponiveis = usados.filter((t) => conhecidos.has(t) && !suportados.has(t));

  return { tokensInvalidos, tokensIndisponiveis };
}

/** Dados de exemplo (para a pré-visualização do editor). */
export function construirDadosExemplo(): Record<string, string> {
  const dados: Record<string, string> = {};
  TOKENS_DOCUMENTO.forEach((t) => {
    dados[t.tag.replace(/[{}]/g, '')] = t.exemplo;
  });
  return dados;
}
